import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, HelpCircle, Trash2 } from "lucide-react";

/**
 * Janela de confirmação no visual da plataforma (substitui o window.confirm do navegador).
 * Uso: if (!(await confirmar("Excluir a aula X?"))) return;
 *      confirmar(texto, { titulo, confirmar: "Excluir", cancelar: "Voltar", perigo: true })
 * O <ConfirmHost /> fica montado uma vez no App.
 */
let abrir = null;

// verbos que viram o texto do botão (ex.: "Excluir a oferta…?" -> botão "Excluir")
const VERBOS = ["Excluir", "Remover", "Apagar", "Desativar", "Desconectar", "Revogar", "Cancelar", "Encerrar", "Bloquear", "Restaurar",
  "Reativar", "Ativar", "Gerar", "Enviar", "Reenviar", "Liberar", "Emitir", "Aprovar", "Recusar", "Transformar", "Tornar", "Duplicar",
  "Substituir", "Limpar", "Zerar", "Marcar", "Desmarcar", "Concluir", "Publicar", "Despublicar", "Trocar", "Mover", "Sair", "Descartar"];
const PERIGO = /^(Excluir|Remover|Apagar|Desativar|Desconectar|Revogar|Cancelar|Encerrar|Bloquear|Recusar|Limpar|Zerar|Descartar|Substituir)\b/i;

export function confirmar(texto, opts = {}) {
  if (!abrir) return Promise.resolve(window.confirm(texto));   // segurança: se o host não estiver montado
  return new Promise((resolve) => abrir({ texto: String(texto || ""), opts, resolve }));
}

export function ConfirmHost() {
  const [pedido, setPedido] = useState(null);
  const okRef = useRef(null);
  useEffect(() => { abrir = (p) => setPedido(p); return () => { abrir = null; }; }, []);
  useEffect(() => { if (pedido) setTimeout(() => okRef.current?.focus(), 30); }, [pedido]);
  useEffect(() => {
    if (!pedido) return undefined;
    const tecla = (e) => { if (e.key === "Escape") fechar(false); };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  });
  if (!pedido) return null;

  const { texto, opts } = pedido;
  const linhas = texto.split(/\n\s*\n/);
  const primeira = linhas[0].trim();
  const verbo = VERBOS.find((v) => primeira.toLowerCase().startsWith(v.toLowerCase() + " ") || primeira.toLowerCase().startsWith(v.toLowerCase() + "?"));
  const perigo = opts.perigo ?? PERIGO.test(primeira);
  const titulo = opts.titulo || (perigo ? "Tem certeza?" : "Confirmar");
  const ok = opts.confirmar || verbo || "Confirmar";
  const nao = opts.cancelar || (ok === "Cancelar" ? "Voltar" : "Cancelar");
  const Icone = perigo ? (/^(Excluir|Remover|Apagar)/i.test(primeira) ? Trash2 : AlertTriangle) : HelpCircle;
  function fechar(v) { pedido.resolve(v); setPedido(null); }

  return (
    <div className="fixed inset-0 z-[200] grid place-items-center p-4 bg-[#0F1730]/55 backdrop-blur-[2px] animate-in fade-in duration-150"
      style={{ pointerEvents: "auto" }}   // continua clicável mesmo aberta por cima de outra janela (que bloqueia cliques fora dela)
      onMouseDown={(e) => { if (e.target === e.currentTarget) fechar(false); }} role="presentation" data-testid="confirm-dialog">
      <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-titulo" aria-describedby="confirm-texto"
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="h-1.5" style={{ background: "linear-gradient(90deg,#232D4F,#1E9E2A)" }} />
        <div className="p-6 flex gap-4">
          <div className={`h-11 w-11 shrink-0 rounded-full grid place-items-center bg-green-50 text-[#1E9E2A]`}>
            <Icone className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="confirm-titulo" className="font-display text-lg font-bold text-slate-900">{titulo}</h2>
            <div id="confirm-texto" className="mt-1.5 space-y-2 text-sm leading-relaxed text-slate-600">
              {linhas.map((l, i) => <p key={i} className={`whitespace-pre-line break-words ${i === 0 ? "text-slate-700" : ""}`}>{l.trim()}</p>)}
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button type="button" onClick={() => fechar(false)} data-testid="confirm-cancel"
            className="h-10 px-4 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300">{nao}</button>
          <button type="button" ref={okRef} onClick={() => fechar(true)} data-testid="confirm-ok"
            className={`h-10 px-5 rounded-lg text-sm font-semibold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 bg-[#1E9E2A] hover:bg-green-700 focus-visible:ring-green-400`}>{ok}</button>
        </div>
      </div>
    </div>
  );
}
